import { action, computed, flow, observable, makeObservable } from "mobx"
import { rootStore } from "store/RootStore"
import { SourceTypeList } from "store/page/player/source/SourceTypeList"
import { alias, list, object, /*createModelSchema,*/ getDefaultModelSchema, serializable, deserialize, primitive } from "serializr"
import { appFeatures } from "AppFeatures"
import { appStats } from "utils/TestSupport"
import { moment } from "store/util/moment"
import { TIME_FORMAT_BACKEND } from "store/model/Time"
import { serviceUrls } from "store/qlapi/ServiceUrls"
import { /*getChanges,*/ deepEqual } from "utils/CompareObjects"
import {channelId, contentId, eventId, startTs, endTs } from "utils/EventHelpers"
import { sleep } from "utils/Utils"

import { getCoCoModule } from "store/page/repo/CoCoModule"
const CoCo = await getCoCoModule()

class Bookmark {
   @serializable eventId = null
   @serializable contentId = null
   @serializable recordingId = null // optional?
   @serializable position = null
   @serializable channelId = null
   @serializable hidden = false
}

class BookmarksList {
   @serializable(list(object(Bookmark))) items = null
}

class Watched {
   @serializable contentId = null
}

class WatchedList {
   @serializable(list(object(Watched))) items = null
}

export class CoCoBookmarks {
   bookmarks = []
   previousBookmarks = []
   currentEvent = null
   _ignoreEvent = null
   progressStarted = false
   refreshCount = 0
   refreshId = null

   constructor() {
      makeObservable(this, {
         bookmarks: observable,
         refreshCount: observable,
         init: action,
         getBookmarksAsync: action,
         clearBookmark: action,
      })
   }

   clearBookmark() {
      console.debug("clearBookmark")
      this.progressStarted = false
   }

   setIgnoreEvent(event) {
      this._ignoreEvent = event
   }

   _convertBookmarkPosition(cocoObj) {
      // we cannot just convert the BigInt number to Number and assign it to cocoObj
      // it's a setter in CoCo and CoCo would generate an error because it wants a BigInt
      return Object.assign({}, {
         eventId: cocoObj.eventId,
         contentId: cocoObj.contentId,
         recordingId: cocoObj.recordingId,
         position: Number(cocoObj.position),
         channelId: cocoObj.channelId,
         hidden: cocoObj.hidden,
      })
   }

   init = flow(function* () {
      this.bookmarks = []
      this.currentEvent = null
      this._ignoreEvent = null
      this.progressStarted = false
      this.refreshCount = 0
      // timeout will be cleared in setupPeriodicRefresh
      //this.refreshId = null
      yield this.version()
      this.setupPeriodicRefresh(true)
   })

   setupPeriodicRefresh(enable) {
      console.debug("setupPeriodicRefresh", enable, this.refreshId)
      if (appFeatures.bookmarksEnabled) {
         if (enable) {
            clearInterval(this.refreshId)
            this.getBookmarksAsync(true)
            this.refreshId = setInterval(() => {
               this.getBookmarksAsync(true)
            }, 20000)
         }
         else {
            clearInterval(this.refreshId)
            this.refreshId = null
         }
      }
   }

   version = flow(function* () {
      try {
         console.debug("CoCo v%s", CoCo.version())
         let result = yield CoCo.apiFetchBookmarksVersionRaw()
         // CoCo returns string instead of object, need to convert to object
         if (typeof result === "string") result = JSON.parse(result)
         const bmVersion = `${result["build_tag"]} (${result["build_date"]})`
         appStats.bookmarks = bmVersion
         console.debug("bookmarks version", bmVersion)
         return bmVersion
      }
      catch (err) {
         console.error("Bookmarks.version", err)
         appStats.bookmarks = err + ` (${serviceUrls.bookmarks})`
         return "undefined"
      }
   })

   getBookmarksAsync = flow(function* (reload) {
      // MTVW-980: CoCo 0.7.300 to 0.8.1 use always cache
      //reload = false
      //console.trace("getBookmarksAsync")
      const requestId = {id: this.refreshCount, reload: reload}
      console.debug("bookmarks loadReq initial %o", requestId)

      if (appFeatures.bookmarksEnabled) {
         try {
            let result = yield CoCo.fetchBookmarks(reload ? CoCo.FetchStrategy.NetworkFirst : CoCo.FetchStrategy.CacheFirst)
            for (let i = 0; i < result.length; i++) {
               result[i] = this._convertBookmarkPosition(result[i])
            }
            // MTVW-947: compare bookmarks to reduce re-renders
            const equal = deepEqual(result, this.previousBookmarks)
            //console.debug("bookmarks equal", equal)
            // MTWW-947: important, do not update discover while in overlay, don't include rootStore.page.Discover.isShow
            const isModalOpen = rootStore.page.OverlayEventDetails.isShow //|| rootStore.page.Discover.isShow
            // make sure that refreshCount initially is incremented,
            // otherwise there are no play buttons if the user hasn't bookmarks yet!
            if (reload && !equal && !isModalOpen || this.refreshCount === 0) {
               // necessary??
               yield rootStore.page.CoCoDiscover.discover(false)
               this.bookmarks = result
               this.refreshCount++
               console.debug("getBookmarksAsync", reload, result.length)
            }
            this.previousBookmarks = result
         }
         catch (err) {
            // make sure that refreshCount initially is incremented,
            // otherwise there are no play buttons if the user hasn't bookmarks yet!
            this.refreshCount++
            console.error("Bookmarks.getBookmarksAsync", err)
         }
      }
      console.debug("bookmarks loadReq %o, refreshCount %o", requestId, this.refreshCount)
      return this.bookmarks
   })

   setEpgEventBookmarkAsync = flow(function* (event, reload) {
   })

   getBookmarkAsync = flow(function* (event, reload, _eventId = null, contentId = null) {
      this.currentEvent = event
      let bookmark = null
      const evtId = _eventId ? _eventId : contentId ? contentId : eventId(event)
      //console.debug("getBookmarkAsync", evtId, this._ignoreEvent?.id, this.bookmarks)
      //if (evtId === this._ignoreEvent?.id) debugger
      if (appFeatures.bookmarksEnabled && evtId !== eventId(this._ignoreEvent)) {
         try {
            if (!reload) {
               // optimization: get from already fetched bookmarks for performance reasons and to mitigate re-renders
               let bm = this.bookmarks.filter((item) => { return item.eventId === evtId })
               /*
               if (bm.length === 0 && contentId) {
                  bm = this.bookmarks.filter((item) => { return item.contentId === contentId })
               }
               */
               //if (bm.length > 0) console.debug("HIT getBookmarkAsync", bm)
               if (bm.length > 0) bookmark = bm[0]
            }
            else {
               let result = null
               result = yield CoCo.fetchBookmark(evtId, CoCo.FetchStrategy.NetworkFirst)
               bookmark = this._convertBookmarkPosition(result)
               const index = this.bookmarks.findIndex(item => item.eventId === evtId)
               //console.debug("getBookmarkAsync replace", this.bookmarks[index])
               if (index !== -1) {
                  this.bookmarks[index] = bookmark
               }
               else this.bookmarks.push(bookmark)
               //console.debug("getBookmarkAsync", bookmark, index, this.bookmarks[index])
            }
         }
         catch (err) {
            //console.warn("getBookmarkAsync", err)
         }
      }
      return bookmark
   })

   // Currently not used
   setBookmarkAsync = flow(function* (player, position) {
      const source = player?._source
      if (appFeatures.bookmarksEnabled && (source?.type === SourceTypeList.ChannelReplay || source?.type === SourceTypeList.ContentPlaylist)) {
         try {
            const bmInfo = this._getBmInfo(source, position)
            const result = yield CoCo.setBookmark(bmInfo.channelId, bmInfo.eventId, bmInfo.contentId, bmInfo.recordingId, bmInfo.position, bmInfo.hidden)
            console.debug("setBookmarkAsync result %o", result)
         }
         catch (err) {
            console.error("Bookmarks.setBookmarkAsync", err)
         }
      }
      /*
      if (this.currentBookmark && (source?.type === SourceTypeList.ChannelReplay || source?.type === SourceTypeList.ContentPlaylist)) {
         this.currentBookmark.playbackPosition = playbackPosition
         this.currentBookmark.watchedDuration = watchedDuration
         yield this.setBookmarkAsync(this.currentBookmark)
         // TODO ?
         //this.currentBookmark = null
      }
      // refresh bookmarks
      yield this.getBookmarksAsync(true)
      */
   })

   deleteBookmarksAsync = flow(function* () {
      if (appFeatures.bookmarksEnabled) {
         try {
            let result = yield CoCo.deleteAllBookmarks()
            console.debug("deleteAllBookmarks result %o", result)
            // CoCo returns string instead of object, need to convert to object
            //if (typeof result === "string") result = JSON.parse(result)
            // refresh bookmarks
            yield this.getBookmarksAsync(true)
         }
         catch (err) {
            console.error("Bookmarks.deleteBookmarksAsync", err)
         }
      }
   })

   deleteBookmarkAsync = flow(function* (event, _eventId = null) {
      if (appFeatures.bookmarksEnabled) {
         try {
            const evtId = _eventId ? _eventId : eventId(event)
            let result = yield CoCo.deleteBookmark(evtId)
            console.debug("deleteBookmarkAsync result %o", result)
            //if (typeof result === "string") result = JSON.parse(result)
            // refresh bookmarks
            yield this.getBookmarksAsync(true)
         }
         catch (err) {
            console.error("Bookmarks.deleteBookmarkAsync", err)
         }
      }
   })

   // seriesId may be null
   informRecordingCreated = flow(function* (eventId, recordingId, seriesId) {
      if (appFeatures.bookmarksEnabled) {
         console.debug("notifyRecordingCreated eventId %o, recordingId %o, seriesId %o", eventId, recordingId, seriesId)
         yield CoCo.notifyRecordingCreated(eventId, recordingId, seriesId)
      }
   })

   // seriesId may be null
   informRecordingDeleted = flow(function* (eventIds, recordingIds, seriesId, reload) {
      if (appFeatures.bookmarksEnabled) {
         console.debug("notifyRecordingDeleted eventIds %o, recordingIds %o, seriesId %o", eventIds, recordingIds, seriesId)
         try {
            for (let i = 0; i < eventIds.length; i++) {
               yield CoCo.notifyRecordingDeleted(eventIds[i], recordingIds[i], seriesId)
            }
            // MTVW-998: added, otherwise the recording in the new recordings lane does not disappear
            yield rootStore.page.CoCoDiscover.discover(true)
            //yield this.getBookmarksAsync(reload)
            /*
            if (eventIds.length === 1) {
               console.debug("setTypeContentPlaylist null", eventIds[0])
               rootStore.page.OverlayEventDetails.setTypeContentPlaylist(null)
               yield rootStore.page.OverlayEventDetails.setEpgEventById(eventIds[0], false, {})
            }
            */
         }
         catch (err) {
            console.error("Bookmarks.informRecordingDeleted", err)
         }
      }
   })

   informRecordingDeletedWithoutEvent = flow(function* (recordingId, reload) {
      // unused
   })

   deleteExistingBookmarks = flow(function* (eventIds, reload) {
      if (appFeatures.bookmarksEnabled) {
         for (let i = 0; i < eventIds.length; i++) {
            const bookmark = yield this.getBookmarkAsync(null, reload, eventIds[i])
            if (bookmark) yield this.deleteBookmarkAsync(null, eventIds[i])
         }
      }
   })

   /* unused -> replayGuide
   watchedAsync = flow(function* () {
      if (appFeatures.bookmarksEnabled) {
         try {
            let result = yield fetchWatched()
            console.debug("watchedAsync result %o", result)
            // CoCo returns string instead of object, need to convert to object
            if (typeof result === "string") result = JSON.parse(result)
            // serializr does not support arrays as root, the root target object is expected to be a class
            // -> wrap it
            return deserialize(WatchedList, { items: resultJson }, (err, result) => {
            })
         }
         catch (err) {
            console.error("Bookmarks.watchedAsync", err)
         }
      }
   })
   */

   _getBmInfo(source, position) {
      // marker: If the user is watching the live head and the show is recorded
      const marker = rootStore.page.Recordings.Manager.getMarker(eventId(source.GetEvent?.event))
      const bmInfo = {
         channelId: channelId(source.GetEvent?.event),
         contentId: contentId(source.GetEvent),
         eventId: eventId(source.GetEvent?.event),
         eventEnd: BigInt(endTs(source.GetEvent?.event)),
         eventStart: BigInt(startTs(source.GetEvent?.event)),
         eventType: source.type === SourceTypeList.ChannelLive ? CoCo.EventType.Live : source.type === SourceTypeList.ChannelReplay ? CoCo.EventType.Replay : source.type === SourceTypeList.ContentPlaylist ? CoCo.EventType.Recording : CoCo.EventType.Live,
         position: BigInt(Math.trunc(position)),
         recordingId: source.type === SourceTypeList.ContentPlaylist ? source.id : marker ? marker.id : undefined,
         // MTVW-899: don't hide anymore
         hidden: false
      }
      console.debug("_getBmInfo", source, bmInfo)
      return bmInfo
   }

   _isPositionValid(bmInfo) {
      return bmInfo.position >= bmInfo.eventStart && bmInfo.position <= bmInfo.eventEnd
   }


   // MTVW-816: async sleep to prevent CoCo panic, can be removed again since sleep applied in LoginService
   playbackProgress = flow(function* (player, position) {
      const source = player?._source
      if (source?.isOnDiscover) return
      if (rootStore.page.Player.player.__path !== "playerAds" && appFeatures.bookmarksEnabled) {
         try {
            console.debug("playback progress source", source)
            const bmInfo = this._getBmInfo(source, position)
            console.debug("bmInfo", bmInfo)
            if (this._isPositionValid(bmInfo)) {
               this.progressStarted = true
               console.debug("notifyPlaybackProgress", source, bmInfo.position, moment(Number(bmInfo.position)).utc().format(TIME_FORMAT_BACKEND), bmInfo)
               yield CoCo.notifyPlaybackProgress(bmInfo.channelId, bmInfo.contentId, bmInfo.eventId, bmInfo.eventEnd, bmInfo.eventStart, bmInfo.eventType, bmInfo.position, bmInfo.recordingId)
               //yield sleep(1)
            }
         }
         catch (err) {
            console.error("Bookmarks.playbackProgress", err)
         }
      }
   })


   // MTVW-816: async sleep to prevent CoCo panic, can be removed again since sleep applied in LoginService
   playbackSeek = flow(function* (player, position) {
      const source = player?._source
      if (source?.isOnDiscover) return
      // unreachable panic if !progressStarted
      if (!this.progressStarted) return
      //yield CoCo.notifyPlaybackSeek(channel_id, content_id, event_id, event_end, event_start, event_type, position, recording_id)
      if (rootStore.page.Player.player.__path !== "playerAds" && appFeatures.bookmarksEnabled) {
         try {
            const bmInfo = this._getBmInfo(source, position)
            if (this._isPositionValid(bmInfo)) {
               console.debug("notifyPlaybackSeek", source, bmInfo.position, moment(Number(bmInfo.position)).utc().format(TIME_FORMAT_BACKEND))
               yield CoCo.notifyPlaybackSeek(bmInfo.channelId, bmInfo.contentId, bmInfo.eventId, bmInfo.eventEnd, bmInfo.eventStart, bmInfo.eventType, bmInfo.position, bmInfo.recordingId)
               //yield sleep(1)
            }
         }
         catch (err) {
            console.error("Bookmarks.playbackSeek", err)
         }
      }
   })

   // MTVW-816: async sleep to prevent CoCo panic, can be removed again since sleep applied in LoginService
   notifyPaused = flow(function* (player, position) {
      const source = player?._source
      if (source?.isOnDiscover) return
      // unreachable panic if !progressStarted
      if (!this.progressStarted) return
      //yield CoCo.notifyPlaybackPaused(channel_id, content_id, event_id, event_end, event_start, event_type, position, recording_id)
      if (rootStore.page.Player.player.__path !== "playerAds" && appFeatures.bookmarksEnabled) {
         try {
            const bmInfo = this._getBmInfo(source, position)
            if (this._isPositionValid(bmInfo)) {
               console.debug("notifyPlaybackPaused", source, bmInfo.position, moment(Number(bmInfo.position)).utc().format(TIME_FORMAT_BACKEND))
               yield CoCo.notifyPlaybackPaused(bmInfo.channelId, bmInfo.contentId, bmInfo.eventId, bmInfo.eventEnd, bmInfo.eventStart, bmInfo.eventType, bmInfo.position, bmInfo.recordingId)
               //yield sleep(1)
            }
         }
         catch (err) {
            console.error("Bookmarks.notifyPaused", err)
         }
      }
   })

   // MTVW-816: async sleep to prevent CoCo panic, can be removed again since sleep applied in LoginService
   notifyStopped = flow(function* (player, position) {
      const source = player?._source
      if (!source || source?.isOnDiscover) return
      //yield CoCo.notifyPlaybackStopped(channel_id, content_id, event_id, event_end, event_start, event_type, position, recording_id)
      if (rootStore.page.Player.player.__path !== "playerAds" && appFeatures.bookmarksEnabled) {
         try {
            const bmInfo = this._getBmInfo(source, position)
            if (this._isPositionValid(bmInfo)) {
               console.debug("notifyPlaybackStopped", source, bmInfo.position, moment(Number(bmInfo.position)).utc().format(TIME_FORMAT_BACKEND), bmInfo)
               yield CoCo.notifyPlaybackStopped(bmInfo.channelId, bmInfo.contentId, bmInfo.eventId, bmInfo.eventEnd, bmInfo.eventStart, bmInfo.eventType, bmInfo.position, bmInfo.recordingId)
               //yield sleep(1)
            }
         }
         catch (err) {
            console.error("Bookmarks.notifyStopped", err)
         }
         yield this.getBookmarksAsync(true)
         //yield rootStore.page.Discover.discover(false)
      }
   })
}
